import { toast } from 'react-toastify';

export default {
    toastError(message, exception) {
        toast.error(message);
        if (exception) message += ': ' + exception;
        console.log(message);
    },

    //  Refreshes user and tokens in local storage.  Returns false on failure.
    async refreshAccessToken() {
        let refreshToken = localStorage.getItem('refreshToken');

        if (refreshToken) {
            const response = await fetch('api/token/tokenrefresh',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json; charset=utf-8' },
                    body: JSON.stringify({ 'accessToken': localStorage.getItem('token'), 'refreshToken': refreshToken })
                });

            if (response.ok) {
                const auth = await response.json();

                let user = JSON.stringify(auth.user);
                localStorage.setItem('user', user);
                localStorage.setItem('refreshToken', auth.refreshToken);
                localStorage.setItem('token', auth.token);

                return true;
            }
        }

        // Remove invalid refresh token to avoid repeat calls.
        localStorage.removeItem('refreshToken');
        return false;
    },

    // iVetApiFetch - wraps fetch calls & returns the raw fetch response. If unauthorized on first attempt,
    // we refresh the access token and try again.  bodyObject, if supplied, is JSON-stringified.
    async iVetApiFetch(url, method, bodyObject) {
        // Set up request.
        let fetchConfig = {
            method: method,
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        };

        if (bodyObject) {
            fetchConfig.body = JSON.stringify(bodyObject);
            fetchConfig.headers['Content-Type'] = 'application/json; charset=utf-8';
        }

        // Attempt using access token.
        let response = await fetch(url, fetchConfig);

        // If unauthorized, use refresh token to request new access token.
        const refreshToken = localStorage.getItem('refreshToken');
        if (response.status === 401 && refreshToken) {
            let refreshSuccess = await this.refreshAccessToken();

            if (refreshSuccess) {
                // Attempt again using new access token.
                fetchConfig.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
                response = await fetch(url, fetchConfig);
            }
        }

        return response;
    },

    async isTokenValid() {
        const response = await this.iVetApiFetch('api/token/tokenTest', 'GET');
        return response.ok;
    },

    async getHospitals() {
        try {
            const response = await this.iVetApiFetch('api/hospitals', 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Hospitals.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Hosptials: ", e);
        }
    },

    // Affiliates, ServiceLevels, Hospital Types
    async getLookup(lookupName) {
        try {
            let lookup = lookupName.split(' ').join('');
            const response = await this.iVetApiFetch(`api/lookups/${lookup}`, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError('Unauthorized.');
            }
            else {
                this.toastError(`Error fetching ${lookupName}.`);
            }
        }
        catch (e) {
            this.toastError(`Error fetching  ${lookupName}.`, e);
        }
    },

    async getLookupEditableFields(lookupName) {
        try {
            let lookup = lookupName.split(' ').join('');
            const response = await this.iVetApiFetch(`api/lookups/${lookup}/editablefields`, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError('Unauthorized.');
            }
            else {
                this.toastError(`Error fetching ${lookupName}.`);
            }
        }
        catch (e) {
            this.toastError(`Error fetching  ${lookupName}.`, e);
        }
    },

    async getHospital(nameHash) {
        const response = await this.iVetApiFetch(`api/hospitals/${nameHash}`, 'GET');
        if (response.ok) {
            let data = await response.json();
            cleanHospitalDates(data);

            return data;
        }
        else if (response.status === 401) {
            toast.error("Unauthorized.");
        }
    },

    async getReports(reportType, nameHash) {
        let url = `api/reports`;
        if (reportType || nameHash) url += '?';
        if (reportType) {
            url += `reportType=${reportType}`;
            if (nameHash) url += '&';
        }
        if (nameHash) url += `nameHash=${nameHash}`;

        const response = await this.iVetApiFetch(url, 'GET');
        if (response.ok) {
            let data = await response.json();
            return data;
        }
        else if (response.status === 401) {
            toast.error("Unauthorized.");
        }
        else {
            this.toastError("Unable to get report list.", response);
        }

    },

    async getReport(reportId, rlsValue, reportType) {
        const response = await this.iVetApiFetch(`api/reports/${reportId}/${rlsValue}/${reportType}`, 'GET');

        if (response.ok) {
            let data = await response.json();
            return data;
        }
        else if (response.status === 401) {
            toast.error("Unauthorized.");
        }
        else {
            toast.error("Could not get embedded report.");
            console.log(response);
            return;
        }
    },

    async getIncomeValidation(nameHash) {
        const response = await this.iVetApiFetch(`api/financials/validation?nameHash=${nameHash}`, 'GET');
        if (response.ok) {
            let data = await response.json();
            return data;
        }
        else if (response.status === 401) {
            toast.error("Unauthorized.");
        }
        else {
            toast.error("Could not get income validation.");
            console.log(response);
        }
    },

    // The are all the rows
    async getFinancialMappings(nameHash, filter) {
        let url = `api/financials/mappings?nameHash=${nameHash}&filter=${filter}`;
        const response = await this.iVetApiFetch(url, 'GET');
        if (response.ok) {
            let data = await response.json();
            return data;
        }
        else if (response.status === 401) {
            toast.error("Unauthorized.");
        }
        else {
            this.toastError("Could not get mappings.", response);
        }
    },

    // These are the dropdown values for iVet Line Item
    async getIVet360LineItems() {
        let url = `api/financials/ivet360lineitems`;
        const response = await this.iVetApiFetch(url, 'GET');

        if (response.ok) {
            let data = await response.json();
            return data;
        }
        else {
            toast.error("Could not get line items.");
            console.log(response);
            return;
        }
    },

    async getCodeGroupMappings(nameHash, statusFilter, hospitalRevenueCategoryFilter, hospitalCodeDescriptionFilter) {
        try {
            let url = `api/mappings/codegroups?nameHash=${nameHash}&statusFilter=${statusFilter}`;
            if (hospitalRevenueCategoryFilter)
                url += `&hospitalRevenueCategory=${hospitalRevenueCategoryFilter}`;
            if (hospitalCodeDescriptionFilter)
                url += `&descriptionSearch=${hospitalCodeDescriptionFilter}`;

            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Code Group Mappings.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Code Group Mappings: ", e);
        }
    },

    async getCodeGroupMappingValidation(nameHash) {
        try {
            let url = `api/mappings/codegroupvalidation?nameHash=${nameHash}`;
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Code Group Mapping Validation.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Code Group Mapping Validation: ", e);
        }
    },

    async getRevenueCategoryMappings(nameHash, filter) {
        try {
            let url = `api/mappings/revenuecategories?nameHash=${nameHash}&filter=${filter}`;
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Revenue Category Mappings.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Revenue Category Mappings: ", e);
        }
    },

    async getRevenueCategoryMappingValidation(nameHash) {
        try {
            let url = `api/mappings/revenuecategoryvalidation?nameHash=${nameHash}`;
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Revenue Category Mapping Validation.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Revenue Category Validation: ", e);
        }
    },

    async getUserEditModel(id) {
        try {
            if (id === null)
                id = 0;
            let url = `api/users/${id}`;

            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching User.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching User: ", e);
        }
    },

    async getUsers() {
        try {
            const response = await this.iVetApiFetch('api/users', 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Users.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Users: ", e);
        }
    },

    async getUserSelectOptions(filter) {
        try {
            let url = 'api/userselectoptions';
            if (filter) url += `?filter=${filter}`;
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching User Select Options.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching User Select Options: ", e);
        }
    },

    // Get list if id not specified. Get specific record, if specified.
    async getCallLog(nameHash, id) {
        try {
            let url = `api/hospitals/${nameHash}/calllog`;
            if (id !== undefined && id) url += `'/${id}`;

            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Call.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Call: ", e);
        }
    },

    async getContacts(nameHash) {
        try {
            const response = await this.iVetApiFetch(`api/hospitals/${nameHash}/contacts`, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Contacts.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Contacts: ", e);
        }
    },


    async getProviderMismappingCount() {
        let url = 'api/providermappings/providermismappingcount';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching provider mappings.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching provider mappings: ", e);
        }
    },

    async getMissingItemsEzyVetCount() {
        let url = 'api/revenuemappings/missingitemsezyvetcount';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching missing items.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching missing items: ", e);
        }
    },

    async getMissingItemsVetdataCount() {
        let url = 'api/revenuemappings/missingitemsvetdatacount';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching missing items.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching missing items: ", e);
        }
    },

    async getMissingItemsVetspireCount() {
        let url = 'api/revenuemappings/missingitemsvetspirecount';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching missing items.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching missing items: ", e);
        }
    },

    async getIntraCompanyCustomerMappingsCount() {
        let url = 'api/intracompanycustomermappings/intracompanycustomermappingscount';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching missing items.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching missing items: ", e);
        }
    },

    async getMissingItemsEzyVet() {
        let url = 'api/revenuemappings/missingitemsezyvet';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching missing items.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching missing items: ", e);
        }
    },

    async getMissingItemsVetdata() {
        let url = 'api/revenuemappings/missingitemsvetdata';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching missing items.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching missing items: ", e);
        }
    },

    async getMissingItemsVetspire() {
        let url = 'api/revenuemappings/missingitemsvetspire';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching missing items.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching missing items: ", e);
        }
    },

    async getProviderMismappings() {
        let url = 'api/providermappings/providermismappings';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching provider mappings.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching provider mappings: ", e);
        }
    },


    async getIntraCompanyCustomerMappings() {
        let url = 'api/intracompanycustomermappings/intracompanycustomermappings';

        try {
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching missing items.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching missing items: ", e);
        }
    },

    async getAffiliateGroups() {
        try {
            const response = await this.iVetApiFetch(`api/managegroups/affiliategroups`, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Affiliate Groups.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Affiliate Groups: ", e);
        }
    },

    async getAffiliateGroupHospitals(id, affiliateGroupId) {
        try {
            const response = await this.iVetApiFetch(`api/managegroups/affiliategrouphospitals?id=${id}&affiliateGroupId=${affiliateGroupId}`, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Affiliate Hospitals.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Affiliate Hospitals: ", e);
        }
    },

    async getGpoGroups() {
        try {
            const response = await this.iVetApiFetch(`api/managegroups/gpogroups`, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Gpo Groups.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Gpo Groups: ", e);
        }
    },

    async getGpoGroupHospitals(id, gpoGroupId) {
        try {
            const response = await this.iVetApiFetch(`api/managegroups/gpogrouphospitals?id=${id}&gpoGroupId=${gpoGroupId}`, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Gpo Hospitals.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Gpo Hospitals: ", e);
        }
    },

    async getProviders(nameHash) {
        try {
            const response = await this.iVetApiFetch(`api/hospitals/${nameHash}/providers`, 'GET');
            if (response.ok) {
                let data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error fetching Providers.`);
            }
        }
        catch (e) {
            this.toastError("Error fetching Providers: ", e);
        }
    },

    async saveHospital(hospital) {
        let result = {
            ok: false,
            status: null,
            data: null,
            message: null
        };

        try {
            let url = '/api/hospitals/';
            if (hospital.idiVetUser !== 0) {
                url += hospital.nameHash;
            }
            let method = hospital.idiVetUser === 0 ? 'POST' : 'PUT';

            let response = await this.iVetApiFetch(url, method, hospital);
            result.status = response.status;
            result.ok = response.ok;

            if (response.ok) {
                result.data = await response.json();
                cleanHospitalDates(result.data);
            }
            else if (response.status === 401) {
                result.message = `Unauthorized.`;
            }
            else {
                result.message = `Error saving hospital. ${await response.text()}`;
            }
        }
        catch(e) {
            result.message = `Error saving hospital: ${e}`;
        }

        if (!result.ok) {
            this.toastError(result.message, { autoClose: 7000 });
        }

        return result;
    },

    async saveFinancialMappings(mappings) {
        let url = 'api/financials/mappings';
        const response = await this.iVetApiFetch(url, 'PUT', mappings);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            this.toastError("Unable to update mappings.", await response.text());
            return false;
        }
    },

    async saveLookup(lookupName, items) {
        let url = `api/lookups/${lookupName.split(' ').join('')}`;
        const response = await this.iVetApiFetch(url, 'PUT', items);
        if (response.ok) {
            return;
        }
        else if (response.status === 400) {
            let err = await response.text();
            console.log(`Unable to update ${lookupName}: ${err}`);
            return err;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return 'Unauthorized.';
        }
        else {
            let err = `Unable to update ${lookupName}.`;
            console.log(err);
            return err;
        }
    },

    async saveCodeGroupMappings(mappings) {
        let url = 'api/mappings/codegroups';
        const response = await this.iVetApiFetch(url, 'PUT', mappings);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError("Unable to update mappings. " + text, text);
            return false;
        }
    },

    async saveRevenueCategoryMappings(mappings) {
        let url = 'api/mappings/revenuecategories';
        const response = await this.iVetApiFetch(url, 'PUT', mappings);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError("Unable to update mappings. " + text, text);
            return false;
        }
    },

    async saveUserEditModel(userEditModel) {
        try {
            const response = await this.iVetApiFetch('api/users', 'PUT', userEditModel);
            if (response.ok) {
                let userId = await response.text();
                return userId;
            }
            else {
                let text = await response.text();
                if (response.status === 401) {
                    this.toastError("Unauthorized.");
                }
                else {
                    this.toastError("Unable to save User. " + text, text);
                }
                return userEditModel.id;
            }
        }
        catch (e) {
            this.toastError("Unable to save User: ", e);
        }
    },

    async saveContacts(nameHash, contacts) {
        let url = `api/hospitals/${nameHash}/contacts`;
        const response = await this.iVetApiFetch(url, 'PUT', contacts);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError("Unable to update contacts. " + text, text);
            return false;
        }
    },

    async saveCallLog(callLogEntry, nameHash) {
        if (callLogEntry.contacts.length === 0) {
            this.toastError("Contact must be selected.");
            return;
        }

        try {
            let url = `api/hospitals/${nameHash}/calllog`;
            const response = await this.iVetApiFetch(url, 'PUT', callLogEntry);
            if (response.ok) {
                let callLogEntryId = response.body;
                return callLogEntryId;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Unable to save Call.`);
            }
        }
        catch (e) {
            this.toastError("Unable to save Call: ", e);
        }
    },

    async saveAffiliateGroups(item) {
        let url = `api/managegroups/affiliategroups`;
        const response = await this.iVetApiFetch(url, 'PUT', item);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError(`Unable to update Affiliate Groups. ${text}`, { autoClose: 7000 });
            return false;
        }
    },

    async saveAffiliateGroupHospitals(items) {
        let url = `api/managegroups/affiliategrouphospitals`;
        const response = await this.iVetApiFetch(url, 'PUT', items);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError(`Unable to update Affiliate Group Hospitals. ${text}`, { autoClose: 7000 });
            return false;
        }
    },
    async saveGpoGroups(items) {
        let url = `api/managegroups/gpogroups`;
        const response = await this.iVetApiFetch(url, 'PUT', items);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError(`Unable to update GPO Groups. ${text}`, { autoClose: 7000 });
            return false;
        }
    },

    async saveGpoGroupHospitals(items) {
        let url = `api/managegroups/gpogroupshospitals`;
        const response = await this.iVetApiFetch(url, 'PUT', items);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError(`Unable to update GPO Group Hospitals. ${text}`, { autoClose: 7000 });
            return false;
        }
    },

    async saveProviders(items) {
        let url = 'api/hospitals/providers';
        const response = await this.iVetApiFetch(url, 'PUT', items);
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError(`Unable to update Providers. ${text}`, { autoClose: 7000 });
            return false;
        }
    },

    async deleteCallLog(nameHash, id) {

        try {
            let url = `api/hospitals/${nameHash}/calllog/${id}`;
            const response = await this.iVetApiFetch(url, 'DELETE');
            if (response.ok) {
                return true;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.", url);
                return false;
            }
            else {
                let text = await response.text();
                this.toastError("Unable to delete the call. ", text);
                return false;
            }
        }
        catch (e) {
            this.toastError("Unable to delete the Call: ", e);
            return false;
        }
    },

    async deleteAffiliateGroup(id) {
        let url = `api/managegroups/affiliategroups?id=${id}`;
        const response = await this.iVetApiFetch(url, 'DELETE');
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError("Unable to remove Affilate Group. " + text, text);
            return false;
        }
    },

    async deleteGpoGroup(id) {
        let url = `api/managegroups/gpogroups?id=${id}`;
        const response = await this.iVetApiFetch(url, 'DELETE');
        if (response.ok) {
            return true;
        }
        else if (response.status === 401) {
            this.toastError("Unauthorized.", url);
            return false;
        }
        else {
            let text = await response.text();
            this.toastError("Unable to remove Gpo Group. " + text, text);
            return false;
        }
    },

    async sendPasswordResetEmail(userId) {
        try {
            let url = `api/users/${userId}/sendpasswordresetemail`;
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                return;
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error sending reset email.`);
            }
        }
        catch (e) {
            this.toastError("Error sending reset email: ", e);
        }
    },

    async sendWelcomeEmail(userId) {
        try {
            let url = `api/users/${userId}/sendwelcomeemail`;
            const response = await this.iVetApiFetch(url, 'GET');
            if (response.ok) {
                return await response.text();
            }
            else if (response.status === 401) {
                this.toastError("Unauthorized.");
            }
            else {
                this.toastError(`Error sending welcome email.`);
            }
        }
        catch (e) {
            this.toastError("Error sending welcome email: ", e);
        }
    },

    async clearFinancialData(nameHash) {
        const response = await this.iVetApiFetch(`api/financials/cleardata?nameHash=${nameHash}`, 'DELETE');
        if (response.ok) {
            return;
        }
        else if (response.status === 401) {
            return 'Unauthorized';
        }
        else {
            let err = "Could not clear data.";
            console.log(err);
            return err;
        }
    },

    async clearFinancialDataAndMappings(nameHash) {
        const response = await this.iVetApiFetch(`api/financials/clearall?nameHash=${nameHash}`, 'DELETE');
        if (response.ok) {
            return;
        }
        else if (response.status === 401) {
            return 'Unauthorized';
        }
        else {
            let err = "Could not clear data and mappings.";
            console.log(err);
            return err;
        }
    },

    async reloadGoogleAnalytics(nameHash) {
        const response = await this.iVetApiFetch(`api/hospitals/${nameHash}/reloadgoogleanalytics`, 'POST');
        if (response.ok) {
            return;
        }
        else if (response.status === 401) {
            return 'Unauthorized';
        }
        else if (response.status === 409) {
            // already running
            let err = await response.text();
            console.log(err);
            return err;
        }
        else {
            let err = "Could not reload Google Analytics.";
            console.log(err + " " + await response.text());
            return err;
        }
    },

    async reloadData(nameHash) {
        const response = await this.iVetApiFetch(`api/hospitals/${nameHash}/reloaddata`, 'POST');
        if (response.ok) {
            return;
        }
        else if (response.status === 401) {
            return 'Unauthorized';
        }
        else if (response.status === 409) {
            // already running
            let err = await response.text();
            console.log(err);
            return err;
        }
        else {
            let err = "Could not trigger reloading the data.";
            console.log(err + " " + await response.text());
            return err;
        }
    },

    updateLastLoginDate() {
        this.iVetApiFetch('api/login/updatelastlogindate', 'POST');
    }
};

function cleanHospitalDates(data) {
    // Not sure how to better handle this, but the data must be in the right format for the date picker
    if (data.terminationDate !== null) data.terminationDate = data.terminationDate.substring(0, 10);
    if (data.firstBillDate !== null) data.firstBillDate = data.firstBillDate.substring(0, 10);
    if (data.acquisitionDate !== null) data.acquisitionDate = data.acquisitionDate.substring(0, 10);
    if (data.webLaunchDate !== null) data.webLaunchDate = data.webLaunchDate.substring(0, 10);
    if (data.learningVetStartDate !== null) data.learningVetStartDate = data.learningVetStartDate.substring(0, 10);
    if (data.learningVetTerminationDate !== null) data.learningVetTerminationDate = data.learningVetTerminationDate.substring(0, 10);
}


// WEBPACK FOOTER //
// ./src/ApiService.js